import { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, type SupportedLanguage } from '../i18n/config';

export const useLanguageRedirect = () => {
  const { locale } = useParams<{ locale?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const handleLanguageRedirect = async () => {
      // Handle ?lang= parameter from legacy redirects
      const searchParams = new URLSearchParams(location.search);
      const langParam = searchParams.get('lang');

      if (langParam) {
        // Determine target language: de/en supported, anything else falls back to en
        const targetLang = supportedLanguages.includes(langParam.toLowerCase() as SupportedLanguage)
          ? langParam.toLowerCase()
          : 'en';

        // Remove lang param, preserve other query parameters
        searchParams.delete('lang');
        const remainingParams = searchParams.toString();
        const queryString = remainingParams ? `?${remainingParams}` : '';

        // Build path segments without existing locale prefix
        const legacyPathSegments = location.pathname.split('/').filter(Boolean);
        const legacyFirstSegment = legacyPathSegments[0];
        const hasLocalePrefix = supportedLanguages.includes(legacyFirstSegment as SupportedLanguage);
        const pathWithoutLocale = hasLocalePrefix
          ? '/' + legacyPathSegments.slice(1).join('/')
          : location.pathname;
        const normalizedPath = pathWithoutLocale === '/' ? '' : pathWithoutLocale;

        navigate(`/${targetLang}${normalizedPath}${queryString}${location.hash}`, { replace: true });
        return;
      }

      const pathSegments = location.pathname.split('/').filter(Boolean);
      const firstSegment = pathSegments[0];
      
      // Check if the first segment is a supported language
      const isLanguageInPath = supportedLanguages.includes(firstSegment as SupportedLanguage);
      
      if (!isLanguageInPath) {
        // No language in path, need to add one
        let detectedLang: string = 'de'; // Default fallback to German
        
        // Try to get language from localStorage first
        const storedLang = localStorage.getItem('i18nextLng');
        if (storedLang && supportedLanguages.includes(storedLang as SupportedLanguage)) {
          detectedLang = storedLang;
        } else {
          // Get browser language
          const browserLang = navigator.language.toLowerCase().split('-')[0];
          if (supportedLanguages.includes(browserLang as SupportedLanguage)) {
            detectedLang = browserLang;
          }
        }
        
        // Construct new path with language
        const newPath = `/${detectedLang}${location.pathname}${location.search}${location.hash}`;
        navigate(newPath, { replace: true });
      } else if (locale && supportedLanguages.includes(locale as SupportedLanguage)) {
        // Language is in path and valid, update i18n if needed
        if (i18n.language !== locale) {
          await i18n.changeLanguage(locale);
          // Update HTML lang attribute when language changes
          document.documentElement.lang = locale;
        }
      }
    };

    handleLanguageRedirect();
  }, [locale, navigate, location, i18n]);
};